import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useThemeMode } from '@rbx/settings';
import {
  getEngineReferenceIcon,
  getEngineReferenceIconSync,
} from '@modules/clients/engineReferenceIconsClient';
import { SvgIconDefaultClass, SvgIconDefaultEnum } from '@modules/ui/icons/EngineReferenceIcons';
import { makeStyles, Typography, useTheme } from '@rbx/ui';
import { ReferenceType } from '@modules/client/types/globalTypes';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import {
  getReferencePageUrl,
  isEngineApiDeprecated,
} from '@modules/apiReference/page/utils/apiReferencePageNavUtils';
import sharedStyles from '@modules/ui/shared/utils/styles';

export interface EngineReferenceStudioIconProps {
  name: string;
  referenceType: ReferenceType;
  showName?: boolean;
  globalFileName?: string; // For Global references, the file name (e.g. "RobloxGlobal")
  showFallbackIcon?: boolean;
}

const useEngineReferenceIconStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0, // Allows flex container to shrink
  },
  icon: {
    width: '20px',
    height: '20px',
    flexShrink: 0, // Prevent icon from shrinking
  },
  textWrapper: {
    ...sharedStyles.textClamp(1),
  },
}));

const getDefaultIcon = (referenceType: ReferenceType): React.ReactElement => {
  switch (referenceType) {
    case ReferenceType.CLASS:
      return <SvgIconDefaultClass />;
    case ReferenceType.ENUM:
      return <SvgIconDefaultEnum />;
    default:
      return <SvgIconDefaultClass />;
  }
};

/**
 * Hook to load engine reference icons with optimized caching.
 * Uses sync check during initialization to prevent flash when manifest is cached,
 * then async fetch in effect to handle cache misses.
 */
const useEngineReferenceIcon = (name: string) => {
  const { themeMode } = useThemeMode();
  // Try to get icon from cache immediately to avoid flash
  const [icon, setIcon] = useState<string | null>(() =>
    getEngineReferenceIconSync(name, themeMode),
  );

  useEffect(() => {
    let cancelled = false;
    getEngineReferenceIcon(name, themeMode).then((iconPath) => {
      if (!cancelled) setIcon(iconPath);
    });
    return () => {
      cancelled = true;
    };
  }, [name, themeMode]);

  return icon;
};

export const EngineReferenceStudioIcon: React.FC<EngineReferenceStudioIconProps> = ({
  name,
  referenceType,
  showName = false,
  globalFileName,
  showFallbackIcon = true,
}) => {
  const { classes } = useEngineReferenceIconStyles();
  const { palette } = useTheme();
  const icon = useEngineReferenceIcon(name);

  let iconElement: React.ReactNode = null;

  if (icon) {
    iconElement = (
      <Image
        src={icon}
        alt={name}
        width={20}
        height={20}
        className={classes.icon}
        // NOTE (tchu, 2025-10-22): Images are already converted to WebP - Next.js optimization would malform the images
        unoptimized
      />
    );
  } else if (showFallbackIcon) {
    iconElement = <span className={classes.icon}>{getDefaultIcon(referenceType)}</span>;
  }

  if (!showName) {
    return iconElement;
  }

  return (
    <span className={classes.container}>
      {iconElement}
      {referenceType !== ReferenceType.GLOBAL && (
        <DynamicallyRouted.Link
          className={classes.textWrapper}
          href={getReferencePageUrl(referenceType, name)}
          color={isEngineApiDeprecated(referenceType, name) ? palette.content.disabled : 'inherit'}>
          <Typography variant='body1' title={name}>
            {name}
          </Typography>
        </DynamicallyRouted.Link>
      )}
      {referenceType === ReferenceType.GLOBAL && globalFileName && (
        <DynamicallyRouted.Link
          className={classes.textWrapper}
          href={getReferencePageUrl(referenceType, globalFileName, `#${name}`)}
          color={
            isEngineApiDeprecated(referenceType, globalFileName, name)
              ? palette.content.disabled
              : 'inherit'
          }>
          <Typography variant='body1' title={name}>
            {name}
          </Typography>
        </DynamicallyRouted.Link>
      )}
      {/* NOTE (tchu, 2025-10-24):
        If globalFileName isn't provided, show the name as a regular text.
        This shouldn't happen, but just in case to avoid errors.
      */}
      {referenceType === ReferenceType.GLOBAL && !globalFileName && (
        <Typography variant='body1' className={classes.textWrapper} title={name}>
          {name}
        </Typography>
      )}
    </span>
  );
};
